import { PageLoader } from '@cosuno/cosuno-ui';
import { generateFrontendRoutes } from '@cosuno/routes';
import React, { memo, type PropsWithChildren, useMemo } from 'react';
import {
  createBrowserRouter,
  type FutureConfig,
  Outlet,
  type RouteObject,
  RouterProvider,
} from 'react-router-dom';

import QueryErrorBoundary from '~/components/QueryErrorBoundary';
import { ErrorBoundary } from '~/shared/components/ErrorBoundary';

import AuthHandler from './AuthHandler';
import CanRedirectToUrlProvider from './CanRedirectToUrlProvider';
import useRouteConfig from './config';
import WorkspaceRedirect from './WorkspaceRedirect';

const routeUrls = generateFrontendRoutes();

type Props = PropsWithChildren<{
  Page: React.ComponentType<PropsWithChildren<{ renderLayout?: boolean }>>;
}>;

const routerFlags = {
  v7_relativeSplatPath: true,
  v7_startTransition: true,
} satisfies FutureConfig;

const Router: React.FC<Props> = ({ children, Page }) => {
  const routeConfig = useRouteConfig(Page, routeUrls);
  const workspaceAndAuth = useMemo<RouteObject>(
    () => ({
      id: 'workspace-and-auth',
      element: (
        <ErrorBoundary>
          <AuthHandler key="auth-handler">
            <WorkspaceRedirect Page={Page}>
              <CanRedirectToUrlProvider routes={routeConfig}>
                <QueryErrorBoundary>
                  <Outlet />
                </QueryErrorBoundary>
              </CanRedirectToUrlProvider>
            </WorkspaceRedirect>
          </AuthHandler>
        </ErrorBoundary>
      ),
      children: routeConfig,
    }),
    [Page, routeConfig],
  );

  const router = useMemo(
    () =>
      createBrowserRouter([
        {
          id: 'root',
          element: children,
          children: [workspaceAndAuth],
        },
      ]),
    [children, workspaceAndAuth],
  );

  return <RouterProvider router={router} future={routerFlags} fallbackElement={<PageLoader />} />;
};

export default memo(Router);
